import { useState } from 'react';
import type { ReactNode } from 'react';
import type { AdminOrgNamespaceRate, SalesOrgNamespaceRate } from '@nadoutong/contracts';
import { Eye, EyeOff, Save, Undo2 } from 'lucide-react';
import { OpsApiError } from '../../api/client.ts';
import { modelPlanManagedFromError } from '../../api/modelPlans.ts';
import {
  namespaceFloorBreachesFromError,
  useUpdateCustomerManagerNamespaceRate,
  useUpdateCustomerManagerNamespaceVisibility,
} from '../../api/customerManagerWorkspace.ts';
import type { NamespaceRateFloorBreach } from '../../api/customerManagerWorkspace.ts';
import { StatusChip } from '../../components/StatusChip.tsx';
import { useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Input, Tooltip } from '../../ui/index.ts';

/**
 * The line-grain header of one namespace group (2026-07-31 design §8).
 *
 * This is where a customer manager expresses the agreement, because that is the
 * grain a commercial agreement is actually struck at: one number for the line,
 * inherited by every model beneath it that carries no exception of its own. The
 * model rows under this header show what each one resolved to, and are the place
 * an individual exception is made or removed.
 *
 * The line's rate deliberately EXCLUDES per-model exceptions. "Your rate on this
 * line" has to be one number; folding the exceptions in would make it
 * un-nameable, since every model on a line can carry a different one.
 */

/**
 * Scope-projected line row: the shared fields plus ONE floor — procurement-
 * derived for the lead/admin view, the lead-set sales floor for the customer-
 * manager view. Mirrors `SkuRateVm` on the model rows so the two grains carry
 * their floor the same way and neither can mislabel the other's layer.
 */
export interface NamespaceRateVm {
  readonly namespace: AdminOrgNamespaceRate | SalesOrgNamespaceRate;
  /** Scope-appropriate binding floor; null = nothing on the line constrains it. */
  readonly floor: number | null;
}

function fmtRate(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(4) : '—';
}

export function NamespaceRateGroup({
  line,
  orgId,
  salesView,
  skuCount,
  vendor,
  namespaceSlug,
  canWrite,
  lockedByPlan,
  onManagedLock,
}: {
  /** Null when the platform sent no line row for this namespace (see below). */
  readonly line: NamespaceRateVm | null;
  readonly orgId: string;
  /** Sales view: the floor caption names the sales floor, never procurement. */
  readonly salesView: boolean;
  readonly skuCount: number;
  readonly vendor: string;
  readonly namespaceSlug: string;
  /**
   * #1958: a read-only session (finance) keeps the line's rate, floor and
   * agreement chip — those are what a credit reviewer came for — and loses
   * the editor, which ops-api would refuse anyway.
   */
  readonly canWrite: boolean;
  /** Plan-hosted org: keep the editors visible but disabled (#2297). */
  readonly lockedByPlan: boolean;
  readonly onManagedLock: (name: string) => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const rateMut = useUpdateCustomerManagerNamespaceRate();
  const visibilityMut = useUpdateCustomerManagerNamespaceVisibility();
  const namespace = line?.namespace ?? null;
  const [value, setValue] = useState(
    namespace === null ? '' : String(namespace.override_rate_multiplier ?? namespace.rate_multiplier),
  );
  const [error, setError] = useState<string | null>(null);
  const [breaches, setBreaches] = useState<readonly NamespaceRateFloorBreach[] | null>(null);
  const busy = rateMut.isPending || visibilityMut.isPending;

  // An agreement exists only at `namespace_override`; the platform arm is a list
  // price nobody signed. Read off `rate_source` rather than
  // `override_rate_multiplier !== null` because the line projection is the one
  // place those two agree today — and relying on the coincidence is how the
  // model rows grew the bug this component exists to fix.
  const agreed = namespace?.rate_source === 'namespace_override';

  function parsedRate(): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  async function write(nextRate: number | null): Promise<void> {
    if (namespace === null) return;
    setError(null);
    setBreaches(null);
    try {
      await rateMut.mutateAsync({
        orgId,
        body: { namespace_id: namespace.namespace_id, rate_multiplier: nextRate },
      });
      toast.success(
        t(
          nextRate === null
            ? 'customer_manager_360.toast.namespace_rate_cleared'
            : 'customer_manager_360.toast.namespace_rate_updated',
        ),
      );
    } catch (err) {
      const normalized = err instanceof Error ? err : new Error(String(err));
      // A refusal on a wide line is unactionable as a bare message, so the
      // breaching models are rendered by name. The raw code stays out of
      // operator-facing copy.
      const floorBreaches = namespaceFloorBreachesFromError(normalized);
      if (floorBreaches !== null) {
        setBreaches(floorBreaches);
        return;
      }
      const managed = modelPlanManagedFromError(normalized);
      if (managed !== null) {
        onManagedLock(managed.model_plan_name);
        return;
      }
      setError(
        normalized instanceof OpsApiError
          ? `${normalized.code}: ${normalized.message}`
          : normalized.message,
      );
    }
  }

  async function toggleVisibility(): Promise<void> {
    if (namespace === null) return;
    setError(null);
    try {
      await visibilityMut.mutateAsync({
        orgId,
        namespaceId: namespace.namespace_id,
        body: { visible: namespace.visible !== true },
      });
      toast.success(t('customer_manager_360.toast.namespace_visibility_updated'));
    } catch (err) {
      const normalized = err instanceof Error ? err : new Error(String(err));
      const managed = modelPlanManagedFromError(normalized);
      if (managed !== null) {
        onManagedLock(managed.model_plan_name);
        return;
      }
      setError(
        normalized instanceof OpsApiError
          ? `${normalized.code}: ${normalized.message}`
          : normalized.message,
      );
    }
  }

  const showBlockedId = `customer-namespace-show-reason-${namespaceSlug}`;
  const editorsEnabled = canWrite && !lockedByPlan;
  const lockHint = t('customer_manager_360.model_plan.lock_tooltip');

  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
      <div className="flex min-w-0 flex-col gap-1">
        <span className="font-mono text-body font-medium text-paper">{namespaceSlug}</span>
        <div className="flex items-center gap-2 text-dense text-paper-low">
          <span className="font-mono">{vendor}</span>
          <span aria-hidden="true">·</span>
          <span>{t('customer_manager_360.sku_rates.sku_count', { count: skuCount })}</span>
        </div>
      </div>

      {namespace === null ? null : (
        <div
          data-testid={`customer-namespace-rate-${namespaceSlug}`}
          className="flex flex-col items-start gap-2 lg:items-end"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-label text-paper-low">
              {t('customer_manager_360.namespace_rate.line_rate')}
            </span>
            <StatusChip
              variant={agreed ? 'ok' : 'neutral'}
              testId={`customer-namespace-rate-state-${namespaceSlug}`}
              label={t(
                agreed
                  ? 'customer_manager_360.namespace_rate.state_agreed'
                  : 'customer_manager_360.namespace_rate.state_platform',
              )}
            />
            <span className="font-mono text-body text-paper">
              {fmtRate(namespace.rate_multiplier)}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-micro text-paper-low">
            <span>
              {t('customer_manager_360.namespace_rate.platform_rate', {
                rate: fmtRate(namespace.platform_rate_multiplier),
              })}
            </span>
            <span aria-hidden="true">·</span>
            <span>
              {line?.floor === null || line === null
                ? t('customer_manager_360.namespace_rate.floor_unset')
                : t(
                    salesView
                      ? 'customer_manager_360.namespace_rate.sales_floor'
                      : 'customer_manager_360.namespace_rate.cost_floor',
                    { floor: fmtRate(line.floor) },
                  )}
            </span>
          </div>

          {canWrite ? (
            <PlanLockWrap locked={lockedByPlan} hint={lockHint}>
              <div className="flex flex-wrap items-end gap-1.5">
                <Input
                  mono
                  type="number"
                  step="0.0001"
                  value={value}
                  onChange={(e) => setValue(e.currentTarget.value)}
                  aria-label={t('customer_manager_360.namespace_rate.line_rate')}
                  className="w-28"
                  disabled={!editorsEnabled}
                  data-testid={`customer-namespace-rate-input-${namespaceSlug}`}
                />
                <Button
                  size="sm"
                  disabled={busy || !editorsEnabled}
                  aria-label={t('customer_manager_360.namespace_rate.save_for', {
                    namespace: namespaceSlug,
                  })}
                  onClick={() => void write(parsedRate())}
                  data-testid={`customer-namespace-rate-save-${namespaceSlug}`}
                >
                  <Save aria-hidden="true" className="h-3.5 w-3.5" />
                  {t('customer_manager_360.namespace_rate.save')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  // Only an agreement can be cleared. Offering it on a platform-priced
                  // line would issue a DELETE matching nothing and report success —
                  // the same silent no-op this task removed from the model rows.
                  disabled={busy || !agreed || !editorsEnabled}
                  aria-label={t('customer_manager_360.namespace_rate.clear_for', {
                    namespace: namespaceSlug,
                  })}
                  onClick={() => void write(null)}
                  data-testid={`customer-namespace-rate-clear-${namespaceSlug}`}
                >
                  <Undo2 aria-hidden="true" className="h-3.5 w-3.5" />
                  {t('customer_manager_360.namespace_rate.clear')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  // Showing a line puts every model beneath it on sale at the line's
                  // rate, so the server refuses unless that rate is agreed. Do not
                  // offer a control the server will reject.
                  disabled={busy || !agreed || !editorsEnabled}
                  aria-describedby={agreed ? undefined : showBlockedId}
                  title={agreed ? undefined : t('customer_manager_360.namespace_rate.show_blocked')}
                  onClick={() => void toggleVisibility()}
                  data-testid={`customer-namespace-visibility-${namespaceSlug}`}
                >
                  {namespace.visible === true ? (
                    <EyeOff aria-hidden="true" className="h-3.5 w-3.5" />
                  ) : (
                    <Eye aria-hidden="true" className="h-3.5 w-3.5" />
                  )}
                  {t(
                    namespace.visible === true
                      ? 'customer_manager_360.namespace_rate.hide_line'
                      : 'customer_manager_360.namespace_rate.show_line',
                  )}
                </Button>
              </div>
            </PlanLockWrap>
          ) : null}

          {canWrite && !agreed ? (
            <span id={showBlockedId} className="text-label text-paper-low">
              {t('customer_manager_360.namespace_rate.show_blocked')}
            </span>
          ) : null}

          {breaches === null ? null : (
            <div
              role="alert"
              data-testid={`customer-namespace-floor-breaches-${namespaceSlug}`}
              className="flex max-w-md flex-col gap-1 rounded-lg border border-warn-line bg-warn-dim/40 px-3 py-2 text-label text-warn"
            >
              <span>{t('customer_manager_360.namespace_rate.breaches_title')}</span>
              <ul className="m-0 flex list-none flex-col gap-0.5 p-0 font-mono">
                {breaches.map((breach) => (
                  <li key={breach.sku_id}>
                    {breach.floor_multiplier === null
                      ? t('customer_manager_360.namespace_rate.breach_row_unknown_floor', {
                          sku: breach.sku_name,
                        })
                      : t('customer_manager_360.namespace_rate.breach_row', {
                          sku: breach.sku_name,
                          floor: fmtRate(breach.floor_multiplier),
                        })}
                  </li>
                ))}
              </ul>
              <span>{t('customer_manager_360.namespace_rate.breaches_hint')}</span>
            </div>
          )}

          {error === null ? null : (
            <span role="alert" className="text-label text-bad">
              {error}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function PlanLockWrap({
  locked,
  hint,
  children,
}: {
  readonly locked: boolean;
  readonly hint: string;
  readonly children: ReactNode;
}): JSX.Element {
  if (!locked) {
    return <>{children}</>;
  }
  return (
    <Tooltip content={hint}>
      <span className="inline-flex">{children}</span>
    </Tooltip>
  );
}
